import { useState } from "react";
import { Button, TextField, Typography, Paper, Box, Fade } from "@mui/material";
import { useNavigate } from "react-router-dom";

import "./LoginPage.css";

import { AUTH_STRINGS, APP_STRINGS, COMMON_STRINGS } from "../../constants";
import { IMAGES } from "../../constants/assets";

import { loginSuccess } from "../../store/authSlice";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

import { sendOtp, verifyOtp } from "../../services/api";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // 🔥 COMMON SNACKBAR HANDLER
  const showMessage = (message: string, type: "success" | "error") => {
    dispatch(showSnackbar({ message, type }));
  };

  // ================= SEND OTP =================
  const handleSendOtp = async () => {
    if (!phone) return showMessage(AUTH_STRINGS.PHONE_REQUIRED, "error");
    if (phone.length !== 10)
      return showMessage(AUTH_STRINGS.ENTER_PHONE_ERROR, "error");

    try {
      setLoading(true);

      const res = await sendOtp(phone);

      showMessage(res.data?.message || AUTH_STRINGS.SEND_OTP, "success");
      setIsOtpSent(true);
    } catch (err: any) {
      showMessage(
        err?.response?.data?.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // ================= VERIFY OTP =================
  const handleVerifyOtp = async () => {
    if (!otp) return showMessage(AUTH_STRINGS.OTP_REQUIRED, "error");

    try {
      setLoading(true);

      const res = await verifyOtp(phone, otp);

      const { token, user } = res.data || {};

      if (!user || user.role !== "admin") {
        return showMessage("Access denied. Admin only.", "error");
      }

      dispatch(
        loginSuccess({
          token,
          user,
        }),
      );
      showMessage(AUTH_STRINGS.LOGIN_SUCCESS, "success");

      navigate("/dashboard"); // ✅ fixed
    } catch (err: any) {
      showMessage(
        err?.response?.data?.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* LEFT */}
      <Box
        className="login-left"
        sx={{
          width: { xs: "0%", md: "50%" },
          display: { xs: "none", md: "flex" },
        }}
      >
        <Box className="login-left-content">
          <img src={IMAGES.LOGO} alt="logo" className="login-left-logo" />

          <Typography variant="h4" className="login-left-title">
            {APP_STRINGS.APP_NAME}
          </Typography>

          <Typography className="login-left-subtitle">
            {AUTH_STRINGS.SUBTITLE}
          </Typography>
        </Box>
      </Box>

      {/* RIGHT */}
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Fade in timeout={600}>
          <Paper
            elevation={4}
            className="login-card"
            sx={{ width: "90%", maxWidth: 400, p: { xs: 3, sm: 4 } }}
          >
            <div className="login-logo">
              <img src={IMAGES.LOGO} alt="logo" />
            </div>

            <Typography variant="h5" className="login-title">
              {AUTH_STRINGS.LOGIN_TITLE}
            </Typography>

            <Typography className="login-subtitle">
              {AUTH_STRINGS.SUBTITLE}
            </Typography>

            <TextField
              fullWidth
              type="tel"
              placeholder={AUTH_STRINGS.PHONE_PLACEHOLDER}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              margin="normal"
            />

            {isOtpSent && (
              <TextField
                fullWidth
                type="tel"
                placeholder={AUTH_STRINGS.OTP_PLACEHOLDER}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                margin="normal"
              />
            )}

            <Button
              fullWidth
              variant="contained"
              className="login-btn"
              disabled={loading || !phone || (isOtpSent && !otp)}
              onClick={isOtpSent ? handleVerifyOtp : handleSendOtp}
            >
              {loading
                ? COMMON_STRINGS.LOADING
                : isOtpSent
                  ? AUTH_STRINGS.VERIFY_OTP
                  : AUTH_STRINGS.SEND_OTP}
            </Button>
          </Paper>
        </Fade>
      </Box>
    </Box>
  );
};

export default LoginPage;
